import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs, { Dayjs } from "dayjs";
import { connect, ConnectedProps } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import "./CopyFromPreviousYear.scss";
import { RootState } from "us.helper/types";
import { nonProductionDays_New } from "us.collection.admin/actions";
import {
  buildCopyCalanderParams,
  mapCopyItemToCopyCalanderRequest,
} from "us.collection.admin/repository/CopyCalendar";
import { DeleteOutlined, ReloadOutlined } from "us.icons";

import {
  $Affix,
  $PageHeader,
  $Divider,
  $Alert,
  $Button,
  $Checkbox,
  $Form,
  $FormItem,
  $Message,
  $Popconfirm,
  $Space,
  $Tag,
  $TableTree,
  $Tooltip,
  $TextArea,
  $RangePicker,
  IOnSort,
} from "us.common/components";
import { CopyCalendarPageValues, CopyItem } from "./types";
import { COPY_DATE_FORMAT, COPY_TYPE_LABELS } from "./constants";
import { CopyTypeKey } from "us.collection.admin/interfaces";
import {
  NON_PROD_DAY_STATUS_COLORS,
  NON_PROD_DAY_STATUS_LABELS,
  NonProdDayStatus,
  RedDay,
} from "us.collection.admin/constants";
import { CopyCalendarValidationSchema } from "./validations";
import {
  getRedDaysForYear,
  overlapsInclusiveByDay,
} from "./functions/functions";
import { handleNonProductionDaysTableFilter } from "us.collection.admin/functions/NonProductionDays/NonProductionDays";

type CopyFromPreviousYearInnerProps = PropsFromRedux & {
  selectedTypes: Record<CopyTypeKey, boolean>;
  setType: (k: CopyTypeKey, next: boolean) => void;
};

/**
 * @description Non Execution Day - Copy From Previous Year Component
 * @link Task - https://unicorn-solutions.atlassian.net/browse/UBX4-27274
 * @author Roshan Maddumage <roshanma@unicorn-solutions.com>
 * @since 24/02/2026
 */
const CopyFromPreviousYearInner: React.FC<CopyFromPreviousYearInnerProps> = (
  props,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    values,
    errors,
    touched,
    isValid,
    validateForm,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext<CopyCalendarPageValues>();
  const [items, setItems] = useState<CopyItem[]>([]);
  const initialItemsRef = useRef<CopyItem[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);
  const expandedInitializedRef = useRef(false);

  const {
    currentDateFormat,
    selectedTypes,
    redDays,
    previousCalendarData,
    copiedCalendarData,
    setType,
    getNonProductionDays,
    getPreviousCalendarData,
    saveCopiedCalendarData,
  } = props;

  // Derive target year from URL query param, defaulting to current year if missing/invalid.
  const targetYear = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const y = sp.get("year");
    const parsed = y ? parseInt(y, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : dayjs().year();
  }, [location.search]);

  const prevYear = targetYear - 1; // Assumption: always copy from the immediately preceding year.

  const isLoading = !!previousCalendarData?.isFetching;
  const isApplying = !!copiedCalendarData?.isSaving;

  const infoText = `${prevYear} ${t(
    "US.COLLECTION.ADMIN:COPY_CALENDAR.COPY_CALENDAR_INFO_FIRST",
  )} ${targetYear} ${t(
    "US.COLLECTION.ADMIN:COPY_CALENDAR.INCLUDING_ALL_RECURRING_HOLIDAYS",
  )}. ${t("US.COLLECTION.ADMIN:COPY_CALENDAR.COPY_CALENDAR_INFO_SECOND")}.`;

  // Formik validation is async and some updates are intentionally non-validating.
  // Centralize the "validate later" behavior so it's consistent everywhere.
  const scheduleValidate = useCallback(() => {
    setTimeout(() => validateForm(), 0);
  }, [validateForm]);

  useEffect(() => {
    // Needed for conflict detection (edited copied rows vs already-saved red days).
    getNonProductionDays?.({ year: `${targetYear}` });
  }, [getNonProductionDays, targetYear]);

  // Reset local state when target year changes.
  // Formik state will be reset by the parent component unmounting this one.
  useEffect(() => {
    setItems([]);
    initialItemsRef.current = [];
    setExpandedRowKeys([]);
    expandedInitializedRef.current = false;
  }, [targetYear]);

  const isSelectedTypesChanged = useMemo(() => {
    return Object.values(selectedTypes).some((v) => v === false);
  }, [selectedTypes]);

  // Used to disable the "Apply" button when no copy types are selected.
  const isSelectedTypesEmpty = useMemo(() => {
    return Object.values(selectedTypes).every((v) => v === false);
  }, [selectedTypes]);

  const setFormikForItem = useCallback(
    (it: CopyItem) => {
      const rangeName = `copy.${it.tempId}.dateRange`;
      const reasonName = `copy.${it.tempId}.reason`;

      const start = dayjs(it.startDate).startOf("day");
      const end = dayjs(it.endDate).startOf("day");
      if (start.isValid() && end.isValid()) {
        setFieldValue(rangeName, [start, end], false);
      } else {
        setFieldValue(rangeName, null, false);
      }

      setFieldValue(reasonName, it.reason ?? "", false);

      (Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[]).forEach((k) => {
        setFieldValue(`copy.${it.tempId}.types.${k}`, !!it.types?.[k], false);
      });
    },
    [setFieldValue],
  );

  // Helper to sync a list of items into Formik state,
  // without triggering validation for each individual update.
  const syncFormikFromItems = useCallback(
    (nextItems: CopyItem[]) => {
      nextItems.forEach((it) => setFormikForItem(it));
    },
    [setFormikForItem],
  );

  // "Apply" button handler - validate, then save to backend if valid.
  const copyAndView = useCallback(async () => {
    const params = buildCopyCalanderParams({
      selectedTypes,
      isRecurring: true,
      currentYear: targetYear,
    });
    getPreviousCalendarData?.(params);
  }, [getPreviousCalendarData, selectedTypes, targetYear]);

  // When previousCalendarData is loaded, transform it into local state and sync to Formik.
  useEffect(() => {
    // Defensive parsing/normalization, since we don't have strict control over the API response format.
    const raw = (previousCalendarData?.data ?? []) as any[];
    if (!Array.isArray(raw) || raw.length === 0) {
      setItems([]);
      initialItemsRef.current = [];
      setExpandedRowKeys([]);
      expandedInitializedRef.current = false;
      setFieldValue("copy", {}, false);
      // Keep isValid in sync with the cleared data.
      scheduleValidate();
      return;
    }

    // The API response is not guaranteed to be in the correct format,
    // so we defensively parse/normalize it into our expected shape.
    const next: CopyItem[] = raw.map((d: any, idx: number) => {
      const parsedStart = dayjs(d?.startDate).startOf("day");
      const parsedEnd = dayjs(d?.endDate).startOf("day");

      const types: Record<CopyTypeKey, boolean> = {
        nonPrinting: !!(d?.isNonPrintDay ?? d?.nonPrinting),
        nonSms: !!(d?.isNonSMSDay ?? d?.nonSms),
        nonEmail: !!(d?.isNonEmailDay ?? d?.nonEmail),
        nonEhf: !!(d?.isNonEhfDay ?? d?.nonEhf),
      };
      const item: CopyItem = {
        tempId: `${d?.id ?? "idx"}-${d?.startDate ?? ""}-${
          d?.endDate ?? ""
        }-${idx}`,
        id: typeof d?.id === "number" ? d.id : undefined,
        startDate: parsedStart.isValid()
          ? parsedStart.format(COPY_DATE_FORMAT)
          : "",
        endDate: parsedEnd.isValid() ? parsedEnd.format(COPY_DATE_FORMAT) : "",
        reason: d?.reason ?? "",
        isFixedDate: !!d?.isFixedDate,
        isRecurring: !!d?.isRecurring,
        status: typeof d?.status === "number" ? d.status : 0,
        types,
      };
      return item;
    });

    setFieldValue("copy", {}, false);
    setItems(next);
    initialItemsRef.current = next;

    setTimeout(() => {
      next.forEach((it) => setFormikForItem(it));
      next.forEach((it) =>
        setFieldTouched(`copy.${it.tempId}.types`, true, false),
      );
      // Validate once after bulk-updating form state.
      validateForm();
    }, 0);
  }, [
    previousCalendarData?.data,
    setFieldTouched,
    setFieldValue,
    setFormikForItem,
    scheduleValidate,
    targetYear,
    validateForm,
  ]);

  // Helper to render the copy type checkboxes in the table.
  const typeCheckbox = (k: CopyTypeKey) => (
    <$Checkbox
      name={`selectedTypes.${k}`}
      checked={selectedTypes[k]}
      onChange={(e: any) => setType(k, e.target.checked)}
    >
      {t(COPY_TYPE_LABELS[k])}
    </$Checkbox>
  );

  // Helper to upsert an item into local state, based on its tempId.
  const upsertItem = (item: CopyItem) => {
    setItems((curr) => {
      const existingIdx = curr.findIndex((x) => x.tempId === item.tempId);
      const existing = existingIdx >= 0 ? curr[existingIdx] : undefined;

      // Status comes from the API (New/Exists) and conflict is computed live.
      // Avoid persisting a computed conflict status into local state.
      const normalized: CopyItem = existing
        ? { ...existing, ...item, status: existing.status }
        : item;
      const next =
        existingIdx >= 0
          ? curr.map((x) => (x.tempId === item.tempId ? normalized : x))
          : [normalized, ...curr];
      return next;
    });
  };

  // Extract just the red days for the target year, for use in conflict detection.
  const existingRedDaysForYear = useMemo(() => {
    return getRedDaysForYear((redDays as any)?.data as RedDay[], targetYear);
  }, [redDays, targetYear]);

  // Derived UI-only conflict info (based on edited rows vs existing redDays).
  // We do NOT write the computed conflict status back into `items`.
  const conflictTooltipByTempId = useMemo(() => {
    const result: Record<string, React.ReactNode> = {};
    if (!items.length) return result;

    const fmt = (dateStr: string) => {
      const d = dayjs(dateStr);
      return d.isValid() ? d.format(currentDateFormat) : dateStr;
    };

    const existingSectionById: Record<string, React.ReactNode> = {};
    if (existingRedDaysForYear.length) {
      for (const it of items) {
        const itStart = dayjs(it.startDate).startOf("day");
        const itEnd = dayjs(it.endDate).startOf("day");
        if (!itStart.isValid() || !itEnd.isValid()) continue;

        // Conflict rule: any inclusive date overlap => conflict.
        // Ignore communication channel/type differences entirely.
        // Also avoid self-conflict for an "Exists" row that matches the same persisted record id.
        const conflicts = existingRedDaysForYear.filter((redDay) => {
          if (typeof it.id === "number" && redDay.id === it.id) return false;

          const rdStart = dayjs(redDay.startDate).startOf("day");
          const rdEnd = dayjs(redDay.endDate).startOf("day");
          if (!rdStart.isValid() || !rdEnd.isValid()) return false;

          return overlapsInclusiveByDay(itStart, itEnd, rdStart, rdEnd);
        });

        if (conflicts.length) {
          existingSectionById[it.tempId] = (
            <div>
              <div>
                <strong>
                  {t(
                    "US.COLLECTION.ADMIN:COPY_CALENDAR.CONFLICT_WITH_EXISTING_NON_PRODUCTION_DAY",
                  )}
                </strong>
              </div>
              {conflicts.slice(0, 3).map((redDay) => (
                <div key={redDay.id}>
                  {fmt(redDay.startDate)} - {fmt(redDay.endDate)}
                  {redDay.reason ? `: ${redDay.reason}` : ""}
                </div>
              ))}
              {conflicts.length > 3 ? (
                <div>
                  +{conflicts.length - 3} {t("US.COMMON:COMMON.MORE")}
                </div>
              ) : null}
            </div>
          );
        }
      }
    }

    // Now compute conflicts between the items themselves,
    // since the user may have edited the copied rows in a way that creates new conflicts that didn't exist in the original data.
    const currentConflictsById: Record<
      string,
      {
        other: CopyItem;
      }[]
    > = {};

    for (let i = 0; i < items.length; i++) {
      const a = items[i];
      const aStart = dayjs(a.startDate).startOf("day");
      const aEnd = dayjs(a.endDate).startOf("day");
      if (!aStart.isValid() || !aEnd.isValid()) continue;

      for (let j = i + 1; j < items.length; j++) {
        const b = items[j];
        const bStart = dayjs(b.startDate).startOf("day");
        const bEnd = dayjs(b.endDate).startOf("day");
        if (!bStart.isValid() || !bEnd.isValid()) continue;

        if (!overlapsInclusiveByDay(aStart, aEnd, bStart, bEnd)) continue;

        currentConflictsById[a.tempId] = [
          ...(currentConflictsById[a.tempId] ?? []),
          { other: b },
        ];
        currentConflictsById[b.tempId] = [
          ...(currentConflictsById[b.tempId] ?? []),
          { other: a },
        ];
      }
    }

    // Now we have two sources of conflict info: existing red days vs edited items, and edited items vs each other.
    const currentSectionById: Record<string, React.ReactNode> = {};
    Object.keys(currentConflictsById).forEach((tempId) => {
      const it = items.find((x) => x.tempId === tempId);
      if (!it) return;
      const conflicts = currentConflictsById[tempId] ?? [];
      if (!conflicts.length) return;

      currentSectionById[tempId] = (
        <div>
          <div>
            <strong>{t("US.COLLECTION.ADMIN:COPY_CALENDAR.CONFLICT")}</strong>
          </div>
          {conflicts.slice(0, 3).map(({ other }) => {
            const key = `${other.tempId}-${other.startDate}-${other.endDate}`;
            return (
              <div key={key}>
                {fmt(other.startDate)} - {fmt(other.endDate)}
                {other.reason ? `: ${other.reason}` : ""}
              </div>
            );
          })}
          {conflicts.length > 3 ? (
            <div>
              +{conflicts.length - 3} {t("US.COMMON:COMMON.MORE")}
            </div>
          ) : null}
        </div>
      );
    });

    // Combine the conflict info from both sources.
    // If an item has conflicts from both sources, we show both sections in the tooltip.
    const ids = new Set([
      ...Object.keys(existingSectionById),
      ...Object.keys(currentSectionById),
    ]);
    ids.forEach((id) => {
      const sections = [existingSectionById[id], currentSectionById[id]].filter(
        Boolean,
      );
      if (sections.length === 1) {
        result[id] = sections[0];
      } else if (sections.length > 1) {
        result[id] = (
          <div>
            {sections.map((s, idx) => (
              <div key={idx}>{s}</div>
            ))}
          </div>
        );
      }
    });

    return result;
  }, [currentDateFormat, existingRedDaysForYear, items, t]);

  const effectiveItems = useMemo(() => {
    if (!items.length) return items;

    const existingIds = new Set(
      existingRedDaysForYear
        .map((d) => d.id)
        .filter((id) => typeof id === "number"),
    );

    return items.map((it) => {
      const hasConflict = !!conflictTooltipByTempId[it.tempId];
      if (hasConflict) {
        return it.status === NonProdDayStatus.Conflict
          ? it
          : { ...it, status: NonProdDayStatus.Conflict };
      }

      // If the backend initially marked it as Conflict, allow user edits to resolve it.
      if (it.status === NonProdDayStatus.Conflict) {
        const nextStatus =
          typeof it.id === "number" && existingIds.has(it.id)
            ? NonProdDayStatus.Exists
            : NonProdDayStatus.New;
        return { ...it, status: nextStatus };
      }

      return it;
    });
  }, [conflictTooltipByTempId, existingRedDaysForYear, items]);

  const counts = useMemo(() => {
    const c = { 0: 0, 1: 0, 2: 0 }; // 0=New, 1=Exists, 2=Conflict
    for (const it of effectiveItems) {
      const status = it.status;
      if (status === 0 || status === 1 || status === 2) {
        c[status]++;
      }
    }
    return c;
  }, [effectiveItems]);

  const hasConflicts = (counts[NonProdDayStatus.Conflict] ?? 0) > 0;

  const deleteItem = (tempId: string) => {
    setItems((curr) => curr.filter((x) => x.tempId !== tempId));

    const currentCopy: any = (values as any)?.copy;
    if (currentCopy && typeof currentCopy === "object") {
      const nextCopy = { ...currentCopy };
      delete nextCopy[tempId];
      setFieldValue("copy", nextCopy, true);
    } else {
      // Still force validation state to refresh.
      validateForm();
    }
  };

  // Reset an individual item back to its initial state (as loaded from the API),
  // for the month-by-month reset feature.
  const resetItem = (tempId: string) => {
    const initial = initialItemsRef.current.find((x) => x.tempId === tempId);
    if (!initial) return;
    setItems((curr) => curr.map((x) => (x.tempId === tempId ? initial : x)));
    setFormikForItem(initial);
    // setFormikForItem performs non-validating updates; validate once after.
    scheduleValidate();
  };

  // Helper to determine if an item belongs to a given month (for the month-by-month reset feature).
  const isInMonth = useCallback((it: CopyItem, monthIndex: number) => {
    const d = dayjs(it.startDate);
    const m = d.isValid() ? d.month() : 0;
    return m === monthIndex;
  }, []);

  // Helper to compare two items for equality, for the month-by-month reset feature.
  const itemsEqualForReset = useCallback((a: CopyItem, b: CopyItem) => {
    if (a.startDate !== b.startDate) return false;
    if (a.endDate !== b.endDate) return false;
    if ((a.reason || "") !== (b.reason || "")) return false;
    const keys = Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[];
    for (const k of keys) {
      if (!!a.types?.[k] !== !!b.types?.[k]) return false;
    }
    return true;
  }, []);

  // Helper to determine if any item in a given month has changes compared to the initial loaded state, for the month-by-month reset feature.
  const monthHasChanges = useCallback(
    (monthIndex: number) => {
      const currentMonth = items.filter((x) => isInMonth(x, monthIndex));
      const initialMonth = initialItemsRef.current.filter((x) =>
        isInMonth(x, monthIndex),
      );

      if (currentMonth.length !== initialMonth.length) return true;

      const initialMap = new Map(initialMonth.map((x) => [x.tempId, x]));
      for (const curr of currentMonth) {
        const init = initialMap.get(curr.tempId);
        if (!init) return true;
        if (!itemsEqualForReset(curr, init)) return true;
      }

      return false;
    },
    [isInMonth, items, itemsEqualForReset],
  );

  // Reset all items in a given month back to their initial state,
  // for the month-by-month reset feature.
  const resetMonth = useCallback(
    (monthIndex: number) => {
      // Compute first, then apply state, then sync Formik.
      const initialById = new Map(
        initialItemsRef.current.map((x) => [x.tempId, x]),
      );
      const initialMonth = initialItemsRef.current.filter((x) =>
        isInMonth(x, monthIndex),
      );

      const resetTempIds = new Set(initialMonth.map((x) => x.tempId));
      items.forEach((x) => {
        if (isInMonth(x, monthIndex)) resetTempIds.add(x.tempId);
      });

      const withoutMonth = items.filter((x) => !resetTempIds.has(x.tempId));
      const resetItems: CopyItem[] = [];
      resetTempIds.forEach((id) => {
        const initial = initialById.get(id);
        if (initial) resetItems.push(initial);
      });

      setItems([...withoutMonth, ...resetItems]);
      syncFormikFromItems(resetItems);
      scheduleValidate();
    },
    [isInMonth, items, scheduleValidate, syncFormikFromItems],
  );

  const resetAll = () => {
    setItems([...initialItemsRef.current]);
    syncFormikFromItems(initialItemsRef.current);
    // setFormikForItem performs non-validating updates; validate once after.
    scheduleValidate();
  };

  useEffect(() => {
    const copyValues: any = (values as any)?.copy;
    if (!copyValues) return;

    setItems((curr) => {
      if (!curr.length) return curr;

      let changed = false;
      const next = curr.map((it) => {
        const entry = copyValues?.[it.tempId];
        if (!entry) return it;

        let nextIt = it;

        if (typeof entry.reason === "string" && entry.reason !== it.reason) {
          nextIt = { ...nextIt, reason: entry.reason };
          changed = true;
        }

        const dr = entry.dateRange as any;
        if (Array.isArray(dr) && dr[0] && dr[1]) {
          const s = dayjs(dr[0]).startOf("day");
          const e = dayjs(dr[1]).startOf("day");
          if (s.isValid() && e.isValid()) {
            const startDate = dayjs(s).format(COPY_DATE_FORMAT);
            const endDate = dayjs(e).format(COPY_DATE_FORMAT);
            if (startDate !== it.startDate || endDate !== it.endDate) {
              nextIt = { ...nextIt, startDate, endDate };
              changed = true;
            }
          }
        }

        return nextIt;
      });

      return changed ? next : curr;
    });
  }, [values]);

  // Validation helper to check that all required fields are filled and at least one type is selected for a list of items, before allowing them to be saved.
  const validateRequiredForItems = useCallback(
    async (toValidate: CopyItem[]) => {
      const requiredMsg =
        "US.COLLECTION.VALIDATIONS:REQUIRED.PLEASE_ADD_A_VALUE_INTO_THE_FIELD";
      const typeMsg = t(
        "US.COLLECTION.VALIDATIONS:REQUIRED.PLEASE_SELECT_AT_LEAST_ONE_COMMUNICATION_CHANNEL",
      );

      let hasAnyError = false;
      let hasRequiredError = false;
      let hasTypeError = false;
      for (const it of toValidate) {
        const entry: any = (values as any)?.copy?.[it.tempId] ?? {};
        const dateRange = entry?.dateRange;
        const reason = entry?.reason;
        const types = entry?.types;

        if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
          hasAnyError = true;
          hasRequiredError = true;
          setFieldError(`copy.${it.tempId}.dateRange`, requiredMsg);
          setFieldTouched(`copy.${it.tempId}.dateRange`, true, false);
        }

        if (typeof reason !== "string" || reason.trim().length === 0) {
          hasAnyError = true;
          hasRequiredError = true;
          setFieldError(`copy.${it.tempId}.reason`, requiredMsg);
          setFieldTouched(`copy.${it.tempId}.reason`, true, false);
        }

        const hasAnyType =
          types &&
          typeof types === "object" &&
          Object.values(types).some((v) => v === true);
        if (!hasAnyType) {
          hasAnyError = true;
          hasTypeError = true;
          setFieldError(`copy.${it.tempId}.types`, typeMsg);
          setFieldTouched(`copy.${it.tempId}.types`, true, false);
        } else {
          // Ensure any previous error gets cleared.
          setFieldError(`copy.${it.tempId}.types`, undefined as any);
        }
      }

      if (hasAnyError) {
        $Message.error(
          (hasRequiredError ? (t(requiredMsg) as any) : null) ??
            (hasTypeError ? typeMsg : (t(requiredMsg) as any)),
        );
      }

      return !hasAnyError;
    },
    [setFieldError, setFieldTouched, t, values],
  );

  // Handler for when the user clicks "Apply" - validate the items, then save if valid.
  const applyItems = async (mode: "all" | "newAndExisting") => {
    const toSave = effectiveItems.filter((it) => {
      if (mode === "all") {
        return true;
      }
      return [NonProdDayStatus.New, NonProdDayStatus.Exists].includes(
        it.status,
      );
    });

    const ok = await validateRequiredForItems(toSave);
    if (!ok) return;

    if (toSave.length === 0) {
      $Message.info(
        t("US.COLLECTION.ADMIN:COPY_CALENDAR.NO_ITEMS_TO_SAVE") as any,
      );
      return;
    }

    const request = toSave.map((it) =>
      mapCopyItemToCopyCalanderRequest({
        id: typeof it.id === "number" ? it.id : -1,
        startDate: it.startDate,
        endDate: it.endDate,
        reason: it.reason,
        isRecurring: it.isRecurring,
        status: it.status,
        types: it.types,
      }),
    );

    saveCopiedCalendarData?.({ request, navigate });
  };

  // Prepare the data for the TableTree,
  // grouping items by month and sorting them by start date within each month.
  const tableData = useMemo(() => {
    const monthMap = new Map<number, CopyItem[]>();
    for (const it of effectiveItems) {
      const m = dayjs(it.startDate).isValid() ? dayjs(it.startDate).month() : 0;
      monthMap.set(m, [...(monthMap.get(m) ?? []), it]);
    }

    const months: number[] = [];
    monthMap.forEach((_v, k) => {
      months.push(k);
    });
    months.sort((a, b) => a - b);

    return months.map((monthIndex) => {
      const monthName = dayjs().month(monthIndex).format("MMMM");
      const children = (monthMap.get(monthIndex) ?? []).sort((a, b) =>
        a.startDate.localeCompare(b.startDate),
      );
      return {
        key: `month-${monthIndex}`,
        monthIndex,
        monthName,
        children: children.map((c) => ({
          ...c,
          key: c.tempId,
          // Used for TableTree filter/sort on the "dateRange" column.
          // We sort/filter by the start date (YYYY-MM-DD).
          dateRange: c.startDate,
        })),
      };
    });
  }, [effectiveItems]);

  useEffect(() => {
    if (expandedInitializedRef.current) return;
    if (!tableData.length) return;
    setExpandedRowKeys(tableData.map((x: any) => x.key));
    expandedInitializedRef.current = true;
  }, [tableData]);

  // Helper to determine if there are any changes between the current items and the initially loaded items, for the global "reset all" feature.
  const itemsHaveChanges = useCallback(
    (current: CopyItem[], initial: CopyItem[]) => {
      if (current.length !== initial.length) return true;

      const initialById = new Map(initial.map((x) => [x.tempId, x]));
      const currentById = new Map(current.map((x) => [x.tempId, x]));

      if (initialById.size !== currentById.size) return true;

      let changed = false;
      currentById.forEach((curr, tempId) => {
        if (changed) return;
        const init = initialById.get(tempId);
        if (!init) {
          changed = true;
          return;
        }
        if (!itemsEqualForReset(curr, init)) {
          changed = true;
        }
      });

      return changed;
    },
    [itemsEqualForReset],
  );

  // Determine if there are any changes between the current items and the initially loaded items, for the global "reset all" feature.
  const isDirty = useMemo(() => {
    return itemsHaveChanges(items, initialItemsRef.current);
  }, [items, itemsHaveChanges]);

  // Create a Map of the initial items by tempId, for efficient lookup when resetting individual items or months.
  const initialItemById = useMemo(() => {
    return new Map(initialItemsRef.current.map((x) => [x.tempId, x]));
  }, [items]);

  const columns: any[] = useMemo(
    () => [
      {
        title: t("US.COLLECTION.ADMIN:COPY_CALENDAR.DATE_MONTH"),
        dataIndex: "dateRange",
        key: "dateRange",
        width: 350,
        customSorter: (a: any, b: any) => (a ?? "").localeCompare(b ?? ""),
        customRenderChild: (_text: any, record: CopyItem) => {
          return (
            <$RangePicker
              name={`copy.${record.tempId}.dateRange`}
              currentTimeFormat={currentDateFormat}
              className="non-production-range-picker"
              size="small"
              picker="date"
              formItem={{ required: true }}
              disabledDate={(current: Dayjs) => {
                return current.year() != targetYear;
              }}
              disabled={isLoading || isApplying}
              onChange={(val: any) => {
                const nextRange = val as any;
                if (!nextRange || !nextRange[0] || !nextRange[1]) {
                  upsertItem({
                    ...record,
                    startDate: "",
                    endDate: "",
                  });
                  return;
                }

                const start = dayjs(nextRange[0]).startOf("day");
                const end = dayjs(nextRange[1]).startOf("day");
                if (!start.isValid() || !end.isValid()) return;

                upsertItem({
                  ...record,
                  startDate: dayjs(start).format(COPY_DATE_FORMAT),
                  endDate: dayjs(end).format(COPY_DATE_FORMAT),
                });
              }}
            />
          );
        },
      },
      {
        title: t("US.COLLECTION.ADMIN:COPY_CALENDAR.STATUS"),
        dataIndex: "status",
        key: "status",
        width: 90,
        customFilter: true,
        customSorter: (a: any, b: any) => a - b,
        customRenderChild: (_text: any, record: CopyItem) => {
          const tag = (
            <$Tag
              color={
                NON_PROD_DAY_STATUS_COLORS[record.status as NonProdDayStatus]
              }
            >
              {t(NON_PROD_DAY_STATUS_LABELS[record.status as NonProdDayStatus])}
            </$Tag>
          );

          const tooltip = conflictTooltipByTempId[record.tempId];
          if (record.status === NonProdDayStatus.Conflict && tooltip) {
            return <$Tooltip title={tooltip}>{tag}</$Tooltip>;
          }

          return tag;
        },
      },
      {
        title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.RECURRING"),
        dataIndex: "isRecurring",
        key: "isRecurring",
        width: 110,
        customFilter: true,
        customSorter: (a: any, b: any) => (a ? 1 : 0) - (b ? 1 : 0),
        customRenderChild: (_text: any, record: any) => {
          return (
            <span>{record?.isRecurring ? t("US.COMMON:COMMON.YES") : "-"}</span>
          );
        },
      },
      {
        title: t("US.COLLECTION.ADMIN:COPY_CALENDAR.REASON"),
        dataIndex: "reason",
        key: "reason",
        customFilter: true,
        customSorter: (a: any, b: any) => a?.localeCompare(b),
        customRenderChild: (_text: any, record: CopyItem) => (
          <$TextArea
            name={`copy.${record.tempId}.reason`}
            required
            autoSize={{ minRows: 1, maxRows: 1 }}
            size="small"
            disabled={isLoading || isApplying}
          />
        ),
      },
      {
        title: t("US.COLLECTION.ADMIN:COPY_CALENDAR.COMMUNICATION_CHANNELS"),
        dataIndex: "types",
        key: "channels",
        align: "left",
        width: 450,
        customRenderChild: (_text: any, record: CopyItem) => {
          const typeError = (errors as any)?.copy?.[record.tempId]?.types;
          const typeTouched = (touched as any)?.copy?.[record.tempId]?.types;

          return (
            <$FormItem
              help={typeTouched && typeError ? t(typeError as any) : null}
              validateStatus={typeTouched && typeError ? "error" : undefined}
            >
              <$Space size={12} wrap>
                {(Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[]).map((k) => (
                  <$Checkbox
                    key={k}
                    checked={!!record.types?.[k]}
                    onChange={(e: any) => {
                      const nextChecked = !!e?.target?.checked;
                      // Mark the group as touched so Yup's object-level test can show feedback.
                      setFieldTouched(
                        `copy.${record.tempId}.types`,
                        true,
                        false,
                      );
                      // Trigger validation immediately.
                      setFieldValue(
                        `copy.${record.tempId}.types.${k}`,
                        nextChecked,
                        true,
                      );
                      upsertItem({
                        ...record,
                        types: { ...record.types, [k]: nextChecked },
                      });
                    }}
                  >
                    {t(COPY_TYPE_LABELS[k])}
                  </$Checkbox>
                ))}
              </$Space>
            </$FormItem>
          );
        },
      },
      {
        title: "",
        key: "actions",
        width: 65,
        customRenderChild: (_text: any, record: CopyItem) => {
          return (
            <$Space>
              <$Tooltip title={t("US.COMMON:COMMON.RESET")}>
                <$Button
                  size="small"
                  icon={<ReloadOutlined />}
                  disabled={
                    isLoading ||
                    isApplying ||
                    !initialItemById.get(record.tempId) ||
                    itemsEqualForReset(
                      record,
                      initialItemById.get(record.tempId) as CopyItem,
                    )
                  }
                  onClick={() => resetItem(record.tempId)}
                />
              </$Tooltip>
              <$Popconfirm
                title={t("COMMON.SURE_TO_DELETE_?")}
                onConfirm={() => deleteItem(record.tempId)}
                placement="topLeft"
              >
                <$Tooltip title={t("US.COMMON:COMMON.DELETE")}>
                  <$Button size="small" danger icon={<DeleteOutlined />} />
                </$Tooltip>
              </$Popconfirm>
            </$Space>
          );
        },
      },
    ],
    [
      currentDateFormat,
      conflictTooltipByTempId,
      deleteItem,
      isApplying,
      isLoading,
      itemsEqualForReset,
      initialItemById,
      resetItem,
      setFieldValue,
      t,
      upsertItem,
    ],
  );

  // Memoized sort handler for the TableTree, to avoid unnecessary re-renders.
  // It applies sorting to both the month groups and the items within each month.
  const handleSort: IOnSort = (sortData, dataSource) => {
    try {
      return sortData(
        dataSource.map((i: any) => {
          return {
            ...i,
            children: sortData(i.children),
          };
        }),
      );
    } catch (error) {
      return dataSource;
    }
  };

  // Memoized filter handler for the TableTree, to avoid unnecessary re-renders.
  const handleFilter = useMemo(
    () => handleNonProductionDaysTableFilter({ t, currentDateFormat }),
    [currentDateFormat, t],
  );

  return (
    <$Form>
      <div className="non-production-days space-content copy-calendar-page">
        <div className="copy-calendar-top">
          <$Affix offsetTop={48}>
            <div className="page-header header-border">
              <div className="d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0"
                    onBack={() => navigate(-1)}
                    title={t("US.COLLECTION.ADMIN:COPY_CALENDAR.COPY_CALENDAR")}
                  />
                  <$Divider className="bui-devider" type="vertical" />
                  <div>
                    <strong>{prevYear}</strong> → <strong>{targetYear}</strong>
                  </div>
                  <$Divider className="bui-devider" type="vertical" />
                  {/* <$Button
                    onClick={() => applyItems("newAndExisting")}
                    disabled={isLoading || isApplying || !isValid}
                    loading={isApplying}
                    size="small"
                    className="mr-2"
                  >
                    {t("US.COLLECTION.ADMIN:COPY_CALENDAR.SAVE_NEW_AND_EXISTS")}
                  </$Button> */}
                  <$Popconfirm
                    title={t(
                      hasConflicts
                        ? "US.COLLECTION.ADMIN:COPY_CALENDAR.ARE_YOU_SURE_TO_SAVE_ALL_INCLUDING_CONFLICTS"
                        : "COMMON.SURE_TO_PROCEED_?",
                    )}
                    placement="topLeft"
                    onConfirm={() => applyItems("all")}
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                    disabled={isLoading || isApplying || !isValid}
                  >
                    <$Button
                      type="primary"
                      disabled={isLoading || isApplying || !isValid}
                      size="small"
                      loading={isApplying}
                    >
                      {t("US.COMMON:COMMON.SAVE")}
                    </$Button>
                  </$Popconfirm>
                </div>
              </div>
            </div>
          </$Affix>

          <div className="copy-calendar-controls-panel mt-3">
            <div className="d-flex justify-content-between mt-3">
              <div>
                <div className="mb-2">
                  <strong>
                    {t(
                      "US.COLLECTION.ADMIN:COPY_CALENDAR.COMMUNICATION_CHANNELS",
                    )}
                  </strong>
                </div>
                <$Space size={16} wrap>
                  {typeCheckbox("nonPrinting")}
                  {typeCheckbox("nonSms")}
                  {typeCheckbox("nonEmail")}
                  {typeCheckbox("nonEhf")}
                </$Space>
              </div>
              <$Alert type="info" showIcon message={infoText} />
            </div>

            <div className="mt-3 d-flex align-items-center justify-content-end">
              {isSelectedTypesChanged && tableData.length > 0 ? (
                <$Popconfirm
                  title={t(
                    "US.COLLECTION.ADMIN:COPY_CALENDAR.COPY_AND_VIEW_CONFIRMATION",
                  )}
                  placement="topLeft"
                  onConfirm={copyAndView}
                  okText={t("US.COMMON:COMMON.YES")}
                  cancelText={t("US.COMMON:COMMON.NO")}
                  disabled={isSelectedTypesEmpty}
                >
                  <$Button
                    type="primary"
                    loading={isLoading || isApplying}
                    disabled={isSelectedTypesEmpty}
                  >
                    {t(
                      "US.COLLECTION.ADMIN:COPY_CALENDAR.CHANGE_SELECTION_AND_RELOAD",
                    )}
                  </$Button>
                </$Popconfirm>
              ) : (
                <$Button
                  type="primary"
                  loading={isLoading || isApplying}
                  onClick={copyAndView}
                  disabled={isSelectedTypesEmpty}
                >
                  {t("US.COLLECTION.ADMIN:COPY_CALENDAR.COPY_AND_VIEW")}
                </$Button>
              )}
            </div>
          </div>

          <$Divider className="my-3" />

          <div className="mt-3 d-flex align-items-center justify-content-between">
            <div>
              <$Tag color={NON_PROD_DAY_STATUS_COLORS[NonProdDayStatus.New]}>
                {t(NON_PROD_DAY_STATUS_LABELS[NonProdDayStatus.New])}:{" "}
                {counts[0]}
              </$Tag>
              <$Tag color={NON_PROD_DAY_STATUS_COLORS[NonProdDayStatus.Exists]}>
                {t(NON_PROD_DAY_STATUS_LABELS[NonProdDayStatus.Exists])}:{" "}
                {counts[1]}
              </$Tag>
              <$Tag
                color={NON_PROD_DAY_STATUS_COLORS[NonProdDayStatus.Conflict]}
              >
                {t(NON_PROD_DAY_STATUS_LABELS[NonProdDayStatus.Conflict])}:{" "}
                {counts[2]}
              </$Tag>
            </div>
            <$Popconfirm
              title={t(
                "US.COMMON:COMMON.ARE_YOU_SURE_YOU_WANT_TO_DISCARD_THE_CHANGES_?",
              )}
              placement="topLeft"
              onConfirm={resetAll}
              okText={t("US.COMMON:COMMON.YES")}
              cancelText={t("US.COMMON:COMMON.NO")}
              disabled={isLoading || isApplying || !isDirty}
            >
              <$Button disabled={isLoading || isApplying || !isDirty}>
                {t("US.COLLECTION.ADMIN:COPY_CALENDAR.RESET_CHANGES")}
              </$Button>
            </$Popconfirm>
          </div>
        </div>

        <div className="pb-3">
          <$TableTree
            rowKey={(record: any) => record.key}
            data={tableData}
            columns={columns}
            colSpan={columns.length}
            renderCellParent={({ row }: any) => (
              <div className="d-flex align-items-center justify-content-between">
                <strong>{row.monthName}</strong>
                <$Popconfirm
                  title={t(
                    "US.COMMON:COMMON.ARE_YOU_SURE_YOU_WANT_TO_DISCARD_THE_CHANGES_?",
                  )}
                  placement="topLeft"
                  onConfirm={() => resetMonth(row.monthIndex)}
                  okText={t("US.COMMON:COMMON.YES")}
                  cancelText={t("US.COMMON:COMMON.NO")}
                  disabled={
                    isLoading || isApplying || !monthHasChanges(row.monthIndex)
                  }
                >
                  <$Tooltip
                    title={t("US.COLLECTION.ADMIN:COPY_CALENDAR.RESET_MONTH")}
                  >
                    <$Button
                      size="small"
                      icon={<ReloadOutlined />}
                      disabled={
                        isLoading ||
                        isApplying ||
                        !monthHasChanges(row.monthIndex)
                      }
                    />
                  </$Tooltip>
                </$Popconfirm>
              </div>
            )}
            bordered
            size="small"
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys: any) => {
                expandedInitializedRef.current = true;
                setExpandedRowKeys(keys);
              },
            }}
            scroll={{ x: "600px", y: "calc(100vh - 200px)" }}
            resetOnSourceChange={true}
            pagination={false}
            onSort={handleSort}
            onFilter={handleFilter}
            filterOnType={true}
          />
        </div>
      </div>
    </$Form>
  );
};

const CopyFromPreviousYear: React.FC<PropsFromRedux> = (props) => {
  const initialValues = useMemo<CopyCalendarPageValues>(
    () => ({
      selectedTypes: {
        nonPrinting: true,
        nonSms: true,
        nonEmail: true,
        nonEhf: true,
      },
      copy: {},
    }),
    [],
  );

  return (
    <Formik<CopyCalendarPageValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => {}}
      validateOnBlur
      validateOnChange
      validationSchema={CopyCalendarValidationSchema}
    >
      {({ values, setFieldValue }) => (
        <CopyFromPreviousYearInner
          {...props}
          selectedTypes={values.selectedTypes}
          setType={(k: CopyTypeKey, next: boolean) =>
            setFieldValue(`selectedTypes.${k}`, next)
          }
        />
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { nonProductionDays, common } = state;
  const { currentDateFormat } = common;
  const {
    redDays: rd,
    previousCalendarData,
    copiedCalendarData,
  } = nonProductionDays;
  return {
    redDays: rd,
    previousCalendarData,
    copiedCalendarData,
    currentDateFormat,
  };
};

const { redDays, previousCalendarData, copiedCalendarData } =
  nonProductionDays_New;

const mapDispatchToProps = {
  getNonProductionDays: redDays.get,
  getPreviousCalendarData: previousCalendarData.get,
  saveCopiedCalendarData: copiedCalendarData.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(CopyFromPreviousYear);
